import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 定数 ---

/**
 * 設問の登録方式（source_type）。
 * master_type ではないためフロント定数で保持する（tech_form_components.md 準拠）。
 */
export const QuestionSourceType = {
  FORM: 'フォーム入力',
  KNZ: 'knz指定',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type QuestionSourceType = keyof typeof QuestionSourceType;

/**
 * 進捗サマリの集計モード（API6341 レスポンスの mode）。
 * バックエンド実装が確定させる（フロントは mode を正とする）。
 */
export const ProgressSummaryMode = {
  ALL: '全課題',
  BY_ASSIGNMENT: '課題別',
  BY_USER: 'ユーザー別',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ProgressSummaryMode = keyof typeof ProgressSummaryMode;

// --- 型定義（課題） ---

/** 課題一覧の1要素（API6305） */
export type AssignmentListResult = {
  assignmentId: string;
  name: string;
  description: string;
  sortOrder: number;
  isHidden: boolean;
  createdAt: string;
  updatedAt: string;
};

/** 課題に含まれる設問（API6304） */
export type AssignmentQuestionResult = {
  questionId: string;
  title: string;
  sourceType: QuestionSourceType;
  /** 対応言語（拡張子）の一覧 */
  extensions: string[];
  sortOrder: number;
};

/** 課題の割当ユーザー（API6304） */
export type AssignmentUserResult = {
  userId: string;
  userName: string;
  createdAt: string;
};

/** 課題詳細（API6304・設問一覧・割当状況込み） */
export type AssignmentDetailResult = {
  assignmentId: string;
  name: string;
  description: string;
  isHidden: boolean;
  /** 課題内の設問（sort_order 順） */
  questions: AssignmentQuestionResult[];
  /** 割当済みユーザー */
  assignedUsers: AssignmentUserResult[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type CreateAssignmentParams = {
  name: string;
  description: string;
};

export type UpdateAssignmentParams = {
  assignmentId: string;
  name: string;
  description: string;
};

export type ListAssignmentParams = {
  keyword?: string;
  includeHidden?: boolean;
};

// --- 型定義（設問） ---

/** 期待値（管理・API6314） */
export type ExpectedResult = {
  sortOrder: number;
  /** 実行時引数（引数なしは null） */
  arguments: string | null;
  expectedStdout: string | null;
  /** 由来の実行履歴（記録用・削除済みは null） */
  sourceFileId: string | null;
  sourceSeq: number | null;
};

/** 正解コード（管理・API6314） */
export type AnswerCodeResult = {
  answerCodeId: string;
  /** 言語（拡張子） */
  extension: string;
  /** 正解コードのファイルID（参照専用） */
  fileId: string;
  sortOrder: number;
  expectedList: ExpectedResult[];
};

/** 設問一覧の1要素（管理・API6315） */
export type QuestionListResult = {
  questionId: string;
  title: string;
  sourceType: QuestionSourceType;
  /** 対応言語（拡張子）の一覧 */
  extensions: string[];
  isHidden: boolean;
  createdAt: string;
  updatedAt: string;
};

/** 設問詳細（管理・API6314） */
export type QuestionDetailResult = {
  questionId: string;
  title: string;
  sourceType: QuestionSourceType;
  /** FORM 時の md 本文（KNZ 時は null） */
  content: string | null;
  /** KNZ 時の knz ファイルID（FORM 時は null） */
  sourceFileId: string | null;
  isHidden: boolean;
  answerCodes: AnswerCodeResult[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

/** 設問登録/更新の期待値入力（由来の実行履歴を指定） */
export type ExpectedInput = {
  /** 由来の実行履歴のファイルID */
  sourceFileId: string;
  /** 由来の実行履歴の連番 */
  sourceSeq: number;
};

/** 設問登録/更新の正解コード入力（fileId ＋ 期待値配列） */
export type AnswerCodeInput = {
  /** 正解コードのファイルID */
  fileId: string;
  /** 期待値（1件以上） */
  expectedList: ExpectedInput[];
};

export type CreateQuestionParams = {
  title: string;
  sourceType: QuestionSourceType;
  /** FORM 時必須（md 本文） */
  content?: string;
  /** KNZ 時必須（knz ファイルの fileId） */
  sourceFileId?: string;
  /** 正解コード（1件以上） */
  answerCodes: AnswerCodeInput[];
};

export type UpdateQuestionParams = {
  questionId: string;
} & Omit<CreateQuestionParams, 'sourceType'>;

export type ListQuestionParams = {
  keyword?: string;
  includeHidden?: boolean;
};

// --- 型定義（進捗） ---

/**
 * 進捗サマリの1要素（API6341）。
 * mode ごとに設定されるフィールドが異なる（未設定は null）。
 */
export type ProgressSummaryResult = {
  mode: ProgressSummaryMode;
  assignmentId: string | null;
  assignmentName: string | null;
  userId: string | null;
  userName: string | null;
  /** ALL のみ: 割当人数 */
  assignedUserCount: number | null;
  /** BY_ASSIGNMENT / BY_USER: クリア済み設問数 */
  clearedQuestionCount: number | null;
  /** 全設問数 */
  totalQuestionCount: number | null;
  /** ALL のみ: 平均クリア率（0.0〜1.0） */
  averageClearRate: number | null;
};

export type ListProgressParams = {
  userId?: string;
  assignmentId?: string;
};

/** 進捗詳細の言語別クリア状況（API6342） */
export type ProgressDetailLanguage = {
  extension: string;
  cleared: boolean;
  firstClearedAt: string | null;
  lastClearedAt: string | null;
  /** クリア時の解答コードスナップショット（未クリアは null） */
  answerDetailSnapshot: string | null;
  /** クリア時の検証ログ（sort_order 順・未クリアは空配列） */
  verificationResults: ProgressVerificationLog[];
};

/** クリア時検証ログの1行（API6342） */
export type ProgressVerificationLog = {
  seq: number;
  arguments: string | null;
  expectedStdout: string | null;
  actualStdout: string | null;
  statusId: number;
  statusDescription: string;
};

/** 進捗詳細の設問ごと（API6342） */
export type ProgressDetailResult = {
  questionId: string;
  title: string;
  languages: ProgressDetailLanguage[];
};

export type GetProgressDetailParams = {
  userId: string;
  assignmentId: string;
};

// --- 共通 ---

const sessionHeaders = () => ({
  headers: {
    'X-Session-Id': localStorage.getItem('session-id'),
  },
});

// --- API関数（課題） ---

/**
 * 課題登録API（API6301 / DEVELOPER）
 * @param params.name - 課題名（必須, 最大128文字）
 * @param params.description - 課題説明（必須, 最大1024文字）
 * @returns 作成した assignmentId
 */
export const createAssignment = async (params: CreateAssignmentParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.CREATE_TRAINING_ASSIGNMENT,
      params,
      sessionHeaders(),
    );
    return response.data.assignmentId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 課題更新API（API6302 / DEVELOPER）
 * 課題名・説明のみ更新する（設問構成は扱わない）。
 * @param params.assignmentId - 課題ID（必須）
 * @param params.name - 課題名（必須, 最大128文字）
 * @param params.description - 課題説明（必須, 最大1024文字）
 */
export const updateAssignment = async (params: UpdateAssignmentParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_TRAINING_ASSIGNMENT, params, sessionHeaders());
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 課題非表示切替API（API6303 / DEVELOPER）
 * 既存の割り当て・進捗には影響しない（新規候補から外すフィルタのみ）。
 * @param assignmentId - 課題ID（必須）
 * @param hidden - true=非表示 / false=再表示（必須）
 */
export const setAssignmentHidden = async (assignmentId: string, hidden: boolean): Promise<void> => {
  try {
    await axios.post(
      KanzhongBackend.HIDDEN_TRAINING_ASSIGNMENT,
      { assignmentId, hidden },
      sessionHeaders(),
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 課題取得API（API6304 / DEVELOPER）
 * 設問一覧・割当状況込みで課題1件を取得する。
 * @param assignmentId - 課題ID（必須）
 */
export const getAssignment = async (assignmentId: string): Promise<AssignmentDetailResult> => {
  try {
    const response = await axios.post(
      KanzhongBackend.GET_TRAINING_ASSIGNMENT,
      { assignmentId },
      sessionHeaders(),
    );
    const raw = response.data.result ?? response.data;
    // バックエンドは hidden（@JsonProperty）/ users で返すため isHidden / assignedUsers に正規化する。
    // 設問・割当が空のとき null / 未設定で返るケースに備え、配列も保証する。
    return {
      ...raw,
      isHidden: raw.isHidden ?? raw.hidden ?? false,
      questions: (raw.questions ?? []).map(
        (q: AssignmentQuestionResult): AssignmentQuestionResult => ({
          ...q,
          extensions: q.extensions ?? [],
        }),
      ),
      assignedUsers: raw.assignedUsers ?? raw.users ?? [],
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 課題一覧取得API（API6305 / DEVELOPER）
 * @param params.keyword - 課題名部分一致（任意）
 * @param params.includeHidden - true で非表示も含む（任意, 既定 false）
 */
export const listAssignment = async (
  params: ListAssignmentParams,
): Promise<AssignmentListResult[]> => {
  try {
    const response = await axios.post(
      KanzhongBackend.LIST_TRAINING_ASSIGNMENT,
      params,
      sessionHeaders(),
    );
    // hidden（@JsonProperty）→ isHidden に正規化
    return (response.data.results ?? []).map(
      (a: AssignmentListResult & { hidden?: boolean }): AssignmentListResult => ({
        ...a,
        isHidden: a.isHidden ?? a.hidden ?? false,
      }),
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

// --- API関数（設問） ---

/**
 * 設問登録API（API6311 / DEVELOPER）
 * FORM / KNZ の2方式。正解コード・期待値をネスト一括で登録する。
 * @param params.title - 設問タイトル（必須, 最大128文字）
 * @param params.sourceType - FORM / KNZ（必須）
 * @param params.content - md 本文（FORM 時必須）
 * @param params.sourceFileId - knz ファイルID（KNZ 時必須）
 * @param params.answerCodes - 正解コード（必須, 1件以上。各正解コードに期待値1件以上）
 * @returns 作成した questionId
 */
export const createQuestion = async (params: CreateQuestionParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.CREATE_TRAINING_QUESTION,
      params,
      sessionHeaders(),
    );
    return response.data.questionId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 設問更新API（API6312 / DEVELOPER）
 * 正解コード・期待値を洗い替え（delete-insert）する。
 * FORM 設問は title/content も更新可、KNZ 設問は正解コード・期待値のみ。
 * @param params.questionId - 設問ID（必須）
 * @returns 対象の questionId
 */
export const updateQuestion = async (params: UpdateQuestionParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.UPDATE_TRAINING_QUESTION,
      params,
      sessionHeaders(),
    );
    return response.data.questionId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 設問非表示切替API（API6313 / DEVELOPER）
 * @param questionId - 設問ID（必須）
 * @param hidden - true=非表示 / false=再表示（必須）
 */
export const setQuestionHidden = async (questionId: string, hidden: boolean): Promise<void> => {
  try {
    await axios.post(
      KanzhongBackend.HIDDEN_TRAINING_QUESTION,
      { questionId, hidden },
      sessionHeaders(),
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 設問取得API（管理・API6314 / DEVELOPER）
 * 正解コード・期待値込みで設問1件を取得する（編集画面用）。
 * @param questionId - 設問ID（必須）
 */
export const getQuestion = async (questionId: string): Promise<QuestionDetailResult> => {
  try {
    const response = await axios.post(
      KanzhongBackend.GET_TRAINING_QUESTION_ADMIN,
      { questionId },
      sessionHeaders(),
    );
    const raw = response.data.result ?? response.data;
    // hidden → isHidden に正規化し、正解コード・期待値の配列を保証する（null / 未設定に備える）
    return {
      ...raw,
      isHidden: raw.isHidden ?? raw.hidden ?? false,
      answerCodes: (raw.answerCodes ?? []).map((ac: AnswerCodeResult): AnswerCodeResult => ({
        ...ac,
        expectedList: ac.expectedList ?? [],
      })),
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 設問一覧取得API（管理・API6315 / DEVELOPER）
 * @param params.keyword - タイトル部分一致（任意）
 * @param params.includeHidden - true で非表示も含む（任意, 既定 false）
 */
export const listQuestion = async (params: ListQuestionParams): Promise<QuestionListResult[]> => {
  try {
    const response = await axios.post(
      KanzhongBackend.LIST_TRAINING_QUESTION_ADMIN,
      params,
      sessionHeaders(),
    );
    // hidden → isHidden に正規化し、extensions を保証する
    return (response.data.results ?? []).map(
      (q: QuestionListResult & { hidden?: boolean }): QuestionListResult => ({
        ...q,
        isHidden: q.isHidden ?? q.hidden ?? false,
        extensions: q.extensions ?? [],
      }),
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

// --- API関数（課題設問・割り当て） ---

/**
 * 課題設問追加API（API6321 / DEVELOPER）
 * 課題に設問を追加する（追加のみ）。
 * @param assignmentId - 課題ID（必須）
 * @param questionIds - 追加する設問IDリスト（必須, 1件以上）
 */
export const addAssignmentQuestions = async (
  assignmentId: string,
  questionIds: string[],
): Promise<void> => {
  try {
    await axios.post(
      KanzhongBackend.ADD_TRAINING_ASSIGNMENT_QUESTION,
      { assignmentId, questionIds },
      sessionHeaders(),
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 課題設問追加取消API（API6322 / DEVELOPER）
 * 課題が未割り当て（割当ユーザーが空）の場合のみ設問の追加を取り消す。
 * 割当済みの場合は 422。
 * @param assignmentId - 課題ID（必須）
 * @param questionIds - 取り消す設問IDリスト（必須, 1件以上）
 */
export const removeAssignmentQuestions = async (
  assignmentId: string,
  questionIds: string[],
): Promise<void> => {
  try {
    await axios.post(
      KanzhongBackend.REMOVE_TRAINING_ASSIGNMENT_QUESTION,
      { assignmentId, questionIds },
      sessionHeaders(),
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 課題割り当てAPI（API6331 / DEVELOPER）
 * 課題をユーザーに追加割り当てする（追加のみ・洗い替えではない）。既割当は重複追加不可。
 * @param assignmentId - 課題ID（必須）
 * @param userIds - 割り当てるユーザーIDリスト（必須, 1件以上）
 */
export const assignAssignment = async (assignmentId: string, userIds: string[]): Promise<void> => {
  try {
    await axios.post(
      KanzhongBackend.ASSIGN_TRAINING_ASSIGNMENT,
      { assignmentId, userIds },
      sessionHeaders(),
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

// --- API関数（進捗） ---

/**
 * 進捗サマリ取得API（API6341 / DEVELOPER・ダイシング）
 * userId / assignmentId で軸を切り替える。両方未指定は全課題サマリ。
 * mode 判定はバックエンドが確定させる（レスポンスの mode を正とする）。
 * @param params.userId - ユーザーID（任意）
 * @param params.assignmentId - 課題ID（任意）
 */
export const listProgressSummary = async (
  params: ListProgressParams,
): Promise<ProgressSummaryResult[]> => {
  try {
    const response = await axios.post(
      KanzhongBackend.LIST_TRAINING_PROGRESS,
      params,
      sessionHeaders(),
    );
    // API6341 は mode をトップレベルで返す（各 result 行には含まれない）。
    // 画面が mode ごとに表示を出し分けるため、各行に mode を付与する。
    const mode: ProgressSummaryMode = response.data.mode ?? 'ALL';
    return (response.data.results ?? []).map((r: ProgressSummaryResult): ProgressSummaryResult => ({
      ...r,
      mode: r.mode ?? mode,
    }));
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 進捗詳細取得API（API6342 / DEVELOPER・ドリルダウン）
 * 特定ユーザー×特定課題の設問×言語のクリア状況を取得する。
 * @param params.userId - ユーザーID（必須）
 * @param params.assignmentId - 課題ID（必須）
 */
export const getProgressDetail = async (
  params: GetProgressDetailParams,
): Promise<ProgressDetailResult[]> => {
  try {
    const response = await axios.post(
      KanzhongBackend.GET_TRAINING_PROGRESS_DETAIL,
      params,
      sessionHeaders(),
    );
    return response.data.results ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
